import { decodeTime } from 'ulid';

import { LIVE_MESSAGE_EVENT } from '../components/chat/events';
import db from './db';
import { currentProfileId } from './meta';
import { containsOnlyEmojiEntities, containsOnlyEntities, containsOnlyWhitespace } from './stringUtils';

export const OPTIMISTIC_MESSAGE_MAX_RETRIES = 3;

export const SENDING_STATUS = 'sending';
export const COMPLETED_STATUS = 'completed';
export const FAILED_STATUS = 'failed';
export const WAITING_FOR_ATTACHMENTS_STATUS = 'waiting_for_attachments';
export const FAILED_ATTACHMENT_STATUS = 'failed_attachment';

export function optimisticMessageId(optimisticSortOrder) {
  return `optimistic_${optimisticSortOrder}`;
}

export function optimisticMessageOwnerId(channelId, optimisticSortOrder) {
  // Upload rows are reassigned to this explicit optimistic-message owner after send.
  return `optimistic-message:${channelId || 'unknown'}:${optimisticSortOrder}`;
}

export function resolveOptimisticSortOrder(message = {}) {
  return message.optimistic_sort_order || message.sort_order || message.id?.replace('optimistic_', '') || null;
}

export function getUploadIds(message = {}) {
  return Array.isArray(message.upload_ids) ? message.upload_ids.filter(Boolean) : [];
}

export function buildOptimisticMessage({
  text = '',
  attachments = [],
  channelId,
  workspaceId,
  optimisticSortOrder,
  replyToMessageId = null,
  status = SENDING_STATUS,
  uploadIds = [],
}) {
  if (!workspaceId) throw new Error('workspaceId is required to build an optimistic message');

  const normalizedText = typeof text === 'string' ? text : '';
  const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
  const normalizedUploadIds = Array.isArray(uploadIds) ? uploadIds.filter(Boolean) : [];
  const optimisticData = {
    id: optimisticMessageId(optimisticSortOrder),
    workspace_id: String(workspaceId),
    pseudotype: 'optimistic',
    channel_id: channelId,
    optimistic_sort_order: optimisticSortOrder,
    text: normalizedText,
    sort_order: optimisticSortOrder,
    timestamp: Math.floor(decodeTime(optimisticSortOrder) / 1000),
    attachments: normalizedAttachments,
    // Upload-row ids let the optimistic message keep showing progress/errors
    // after the composer has moved on to a new draft.
    upload_ids: normalizedUploadIds,
    status,
    retry_attempt: 0,
    retry_max_attempts: OPTIMISTIC_MESSAGE_MAX_RETRIES,
    next_retry_at: null,
    last_error: null,
    reactions: {},
    mentions: [],
    contains_only_attachments: normalizedAttachments.length > 0 && containsOnlyWhitespace(normalizedText),
    contains_only_entities: containsOnlyEntities(normalizedText),
    contains_only_emoji_entities: containsOnlyEmojiEntities(normalizedText),
  };

  if (replyToMessageId) optimisticData.reply_to_message_id = replyToMessageId;

  if (currentProfileId()) {
    optimisticData.author_type = 'profile';
    optimisticData.author_id = currentProfileId();
  }

  return optimisticData;
}

export async function persistOptimisticMessage(message) {
  window.dispatchEvent(new CustomEvent(LIVE_MESSAGE_EVENT, { detail: { action: 'create', data: message } }));
  await db.messages.put({ ...message, fresh_since: Date.now() });
  return message;
}

export async function createOptimisticMessage(options) {
  return persistOptimisticMessage(buildOptimisticMessage(options));
}
